import { ColumnDetails } from '../../../grid/types/common';
import { ColumnIndexes, ColumnKeys } from './gridConfig/companies-grid-columns';
import { Company } from '../types/companies-grid-company';
import { LicenseRenderers } from '../../../company/common/licenseRenderers/LicenseRenderers';
import { SubscriptionType } from '../../../license/types/SubscriptionType';
import { gridCustomHtmlRenderer } from '../../../grid/utils/renderers';
import { LicenseProductStatus, LicenseUsage } from '../../../company/common/licensingGrid/types/licensing-grid-license';
import { PaymentPlanType } from '../../../company/types/PaymentPlanType';
import { TemplateResult, html } from 'lit';
import { isLv2License } from '../../../license/common/licenseHelper';
import { MspInProductTrialStatus } from '../../../company/types/msp-in-product-trial-status';
import { translateFn } from '../../../utils/translator.helper';

export function renderCompanyName(
  dispatchClickEventFn: (companyId: string, companyName: string) => void,
  rowData,
  columnDetails: ColumnDetails<ColumnIndexes, ColumnKeys>
): TemplateResult {
  const companyName = rowData.item[columnDetails.key];
  const companyId = rowData.item[ColumnKeys.COMPANY_ID];

  return html`<span
    class="accent cell-label"
    style="cursor: pointer"
    @click="${() => dispatchClickEventFn(companyId, companyName)}"
    >${companyName}</span
  >`;
}

export function renderCompanyProductName(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenseUsage = rowData.item.licenses;

  let cellText = '-';

  if (!licenseUsage?.length) {
    cellText = this.dependencies.translateFn('No products');
  } else if (licenseUsage.length > 1) {
    cellText = this.dependencies.translateFn('{numberOfProducts} products', {
      numberOfProducts: licenseUsage.length,
    });
  } else {
    const [license] = licenseUsage;

    cellText = LicenseRenderers.getTranslatedProductName(
      license.isMonthly ?? false,
      license.isInheriting ?? false,
      license.hasBasicMonthly ?? false,
      license.productId
    );
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>${cellText}</span>`);
}

export function renderCompanyProductType(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses;
  const subscription = rowData.item.subscription;

  switch (subscription) {
    case SubscriptionType.Frat:
      return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('Standard')}</span>`);
    case SubscriptionType.BasicMonthly:
    case SubscriptionType.MonthlyLicenseTrial:
      return gridCustomHtmlRenderer(`<span class='cell-label'>Endpoint Security</span>`);
  }

  if (!licenses || licenses.length === 0) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
  }

  if (licenses.length === 1) {
    const [license] = licenses;

    const productType = LicenseRenderers.getTranslatedProductType(
      license.isMonthly ?? false,
      license.isAddon ?? false,
      license.productType
    );

    return gridCustomHtmlRenderer(`<span class='cell-label'>${productType}</span>`);
  }

  let standardCount = 0,
    addonCount = 0;

  for (const license of licenses) {
    if (license.isAddon) {
      addonCount++;
    } else {
      standardCount++;
    }
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>
    <p>${standardCount} ${this.dependencies.translateFn('Standard')}</p>
    ${
      addonCount > 0
        ? `<p>${addonCount} ${
            addonCount === 1 ? this.dependencies.translateFn('Add-on') : this.dependencies.translateFn('Add-ons')
          }</p>`
        : ''
    }
    </span>`);
}

export function renderCompanyProductStatus(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses;

  if (!licenses || licenses.length === 0) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
  }

  if (licenses.length === 1) {
    const productStatus = licenses[0].productStatus;

    if (productStatus === undefined) {
      return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
    }

    if (productStatus === LicenseProductStatus.INVALID) {
      // render "invalid" as "expired" in the main grid
      return gridCustomHtmlRenderer(
        `<span class='cell-label'>${LicenseRenderers.getTranslatedProductStatus(LicenseProductStatus.EXPIRED)}</span>`
      );
    }

    return gridCustomHtmlRenderer(
      `<span class='cell-label'>${LicenseRenderers.getTranslatedProductStatus(productStatus)}</span>`
    );
  }

  let activeProducts = 0;
  let expiredProducts = 0;
  let trialProducts = 0;

  for (const license of licenses) {
    if (license.productStatus === undefined) {
      continue;
    }

    switch (license.productStatus) {
      case LicenseProductStatus.ACTIVE:
        activeProducts++;
        break;
      case LicenseProductStatus.EXPIRED:
      case LicenseProductStatus.INVALID:
        expiredProducts++;
        break;
      case LicenseProductStatus.TRIAL:
        trialProducts++;
        break;
    }
  }

  let cellHtml = '';

  if (activeProducts > 0) {
    cellHtml += `<p>${activeProducts} ${LicenseRenderers.getTranslatedProductStatus(LicenseProductStatus.ACTIVE)}</p>`;
  }

  if (expiredProducts > 0) {
    cellHtml += `<p>${expiredProducts} ${LicenseRenderers.getTranslatedProductStatus(
      LicenseProductStatus.EXPIRED
    )}</p>`;
  }

  if (trialProducts > 0) {
    cellHtml += `<p>${trialProducts} ${LicenseRenderers.getTranslatedProductStatus(LicenseProductStatus.TRIAL)}</p>`;
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>${cellHtml}</span>`);
}

export function renderCompanyLicenseKey(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses;

  let cellText = '-';

  if (!licenses || licenses.length === 0 || !licenses[0].licenseKey) {
    cellText = '-';
  } else if (licenses.length === 1) {
    cellText = licenses[0].licenseKey;
  } else {
    cellText = this.dependencies.translateFn('{numberOfKeys} license keys', {
      numberOfKeys: licenses.length,
    });
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>${cellText}</span>`);
}

export function renderProductsExpiryDate(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses;

  if (!licenses || licenses.length === 0) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
  }

  if (licenses.length === 1) {
    const expiryDate = licenses[0].expiryDate;
    const productStatus = licenses[0].productStatus;
    const isMonthly = licenses[0].isMonthly;
    const hasMspTrial = licenses[0].hasMspTrial;

    if (productStatus === LicenseProductStatus.EXPIRED) {
      return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('Expired')}</span>`);
    }

    if (isMonthly && !hasMspTrial) {
      return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('Never')}</span>`);
    }

    return gridCustomHtmlRenderer(`<span class='cell-label'>
      ${LicenseRenderers.renderExpiryDate(expiryDate, this.dependencies.getTimezone())}
    </span>`);
  }

  let validProducts = 0,
    expiredProducts = 0,
    noExpireProducts = 0;

  for (const license of licenses) {
    if (
      license.productStatus === LicenseProductStatus.EXPIRED ||
      license.productStatus === LicenseProductStatus.INVALID
    ) {
      expiredProducts++;
      continue;
    }

    if (license.isMonthly) {
      noExpireProducts++;
      continue;
    }

    validProducts++;
  }

  let cellHtml = '';

  if (validProducts > 0) {
    cellHtml += `<p>${validProducts} ${this.dependencies.translateFn('Valid')}</p>`;
  }

  if (noExpireProducts > 0) {
    cellHtml += `<p>${noExpireProducts} ${this.dependencies.translateFn('Never')}</p>`;
  }

  if (expiredProducts > 0) {
    cellHtml += `<p>${expiredProducts} ${this.dependencies.translateFn('Expired')}</p>`;
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>${cellHtml}</span>`);
}

export function renderTotalSeats(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses ?? [];

  const licensesWithUsage: LicenseUsage[] = [];

  for (const license of licenses) {
    const totalSeats = license.totalSeats;

    if (totalSeats === undefined || totalSeats === null) {
      licensesWithUsage.push(license);
      continue;
    }

    if (license.isMonthly) {
      licensesWithUsage.push(license);
    } else if (
      !(
        LicenseRenderers.isAddonWithoutUsage(license.isAddon ?? false, license.productId) ||
        (license.isTrial &&
          license.licenseKey &&
          isLv2License(license.licenseKey) &&
          !LicenseRenderers.isAddonWithUsage(license.productId))
      )
    ) {
      licensesWithUsage.push(license);
    }
  }

  if (licensesWithUsage.length === 0) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
  }

  if (licensesWithUsage.length === 1) {
    const totalSeatsText = LicenseRenderers.renderTotalSeats(licensesWithUsage[0]);
    return gridCustomHtmlRenderer(`<span class='cell-label'>${totalSeatsText}</span>`);
  }

  return gridCustomHtmlRenderer(
    `<span class='cell-label'>${this.dependencies.translateFn('Multiple unit types')}</span>`
  );
}

export function renderUsageBreakdown(rowData: unknown & { item: Company }): string | DocumentFragment {
  const licenses = rowData.item.licenses;

  if (!licenses || licenses.length === 0) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>-</span>`);
  }

  if (licenses.length === 1) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>
      ${LicenseRenderers.renderUsageBreakdown(licenses[0], true, rowData.item.companyType)}
    </span>`);
  }

  const seatTypeCount = LicenseRenderers.getNumberOfSeatTypes(licenses);
  return gridCustomHtmlRenderer(
    `<span class='cell-label'>${this.dependencies.translateFn('{numberOfUsages} usage types', {
      numberOfUsages: seatTypeCount,
    })}</span>`
  );
}

export function renderAutoRenewal(rowData: unknown & { item: Company }): string | DocumentFragment {
  const company = rowData.item;
  const isMonthlyPayment = company.paymentPlan === PaymentPlanType.MONTHLY;
  const [monthlyLicense] = company.licenses ?? [];

  if (!isMonthlyPayment || monthlyLicense === undefined) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  const hasSubscriptionEndDate =
    monthlyLicense.subscriptionEndDate !== undefined && monthlyLicense.subscriptionEndDate !== null;

  if (!hasSubscriptionEndDate) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  return LicenseRenderers.renderAutoRenewal(monthlyLicense);
}

export function renderEndSubscriptionDate(rowData: unknown & { item: Company }) {
  const company = rowData.item;
  const isMonthlyPayment = company.paymentPlan === PaymentPlanType.MONTHLY;
  const [monthlyLicense] = company.licenses ?? [];

  if (!isMonthlyPayment || monthlyLicense === undefined) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  if (!monthlyLicense.isInheriting) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  if (company.subscription === SubscriptionType.InheritedMspTrial) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  return LicenseRenderers.renderSubscriptionEndDate(monthlyLicense, this.dependencies.getTimezone());
}

export function renderMinimumUsage(rowData: unknown & { item: Company }): string | DocumentFragment {
  const company = rowData.item;
  const isMonthlyPayment = company.paymentPlan === PaymentPlanType.MONTHLY;
  const [monthlyLicense] = company.licenses ?? [];

  if (!isMonthlyPayment || monthlyLicense === undefined) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  const minUsage = monthlyLicense.minimalCommitment;

  if (minUsage === undefined || minUsage === null) {
    return gridCustomHtmlRenderer(`<span class='cell-label'>${this.dependencies.translateFn('N/A')}</span>`);
  }

  return gridCustomHtmlRenderer(`<span class='cell-label'>${minUsage}</span>`);
}

function getMspInProductTrialStatusValues(mspInProductTrialStatus: MspInProductTrialStatus | undefined): string {
  switch (mspInProductTrialStatus) {
    case MspInProductTrialStatus.NOT_APPLICABLE:
      return translateFn('Not eligible');
    case MspInProductTrialStatus.TRIAL_AVAILABLE:
      return translateFn('Available for trial');
    case MspInProductTrialStatus.IN_TRIAL:
      return translateFn('Trial ongoing');
    case MspInProductTrialStatus.TRIAL_EXPIRED:
      return translateFn('Trial expired');
    default:
      return translateFn('Not eligible');
  }
}

export function renderMspInProductTrialStatus(rowData: unknown & { item: Company }): string | DocumentFragment {
  const company = rowData.item;
  const mspInProductTrialStatus = company.mspInProductTrialStatus;
  return gridCustomHtmlRenderer(
    `<span class='cell-label'>${getMspInProductTrialStatusValues(mspInProductTrialStatus)}</span>`
  );
}
